import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../../interceptors";
import { SEED_TEMPLATES } from "./seed-templates";

const TEMPLATE_TYPES = [
  { value: "ACCEPTANCE", label: "Interview Acceptance", badge: "badge-brand" },
  { value: "REMINDER", label: "Reminder", badge: "badge-warning" },
  { value: "INTERVIEW_LINK", label: "Interview Link", badge: "badge-success" },
  { value: "DIGEST", label: "Digest", badge: "badge-warning" },
];

const CHANNELS = [
  { value: "EMAIL", label: "Email Templates", icon: "\u2709\uFE0F" },
  { value: "WHATSAPP", label: "WhatsApp Templates", icon: "\uD83D\uDCAC" },
  { value: "SMS", label: "SMS Templates", icon: "\uD83D\uDCF1" },
];

// SMS hard limit (GSM-7 single-segment). Warn before hitting it.
const SMS_LIMIT = 160;
const SMS_WARN = 140;

const PLACEHOLDERS = [
  {
    key: "{{candidateName}}",
    desc: "Candidate's full name",
    example: "Aarav Sharma",
  },
  {
    key: "{{candidateEmail}}",
    desc: "Candidate's email address",
    example: "aarav@gmail.com",
  },
  {
    key: "{{jobTitle}}",
    desc: "Job position title",
    example: "Senior React Developer",
  },
  {
    key: "{{acceptUrl}}",
    desc: "Invite acceptance link",
    example: "https://app.ample.com/invite/abc123",
  },
  {
    key: "{{rejectUrl}}",
    desc: "Invite rejection link",
    example: "https://app.ample.com/invite/abc123/reject",
  },
  {
    key: "{{interviewUrl}}",
    desc: "Interview session link",
    example: "https://app.ample.com/ai-interview/xyz789",
  },
  {
    key: "{{interviewDate}}",
    desc: "Scheduled interview date",
    example: "2026-04-20",
  },
  {
    key: "{{interviewTime}}",
    desc: "Scheduled interview time",
    example: "10:00 AM",
  },
  {
    key: "{{interviewExpiry}}",
    desc: "Invitation expiry date/time",
    example: "2026-04-27 10:00 AM",
  },
  { key: "{{companyName}}", desc: "Your company name", example: "Ample Hire" },
];

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    type: "ACCEPTANCE",
    subject: "",
    body: "",
  });
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [expandedPreview, setExpandedPreview] = useState(null);
  const [toast, setToast] = useState(null);
  const [editorTab, setEditorTab] = useState("write"); // "write" | "preview"
  const [activeChannel, setActiveChannel] = useState("EMAIL");
  const bodyRef = useRef(null);

  useEffect(() => {
    document.title = "Message Templates - Ample Hire";
    loadTemplates();
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const getAllTemplates = () => {
    const stored = localStorage.getItem("ample_templates");
    if (stored) return JSON.parse(stored);
    localStorage.setItem("ample_templates", JSON.stringify(SEED_TEMPLATES));
    return SEED_TEMPLATES;
  };

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const all = getAllTemplates();
      setTemplates(all.filter((t) => t.channel === activeChannel));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [activeChannel]);

  const startCreate = () => {
    setEditing("new");
    const defaultBody =
      activeChannel === "WHATSAPP"
        ? "Hi {{candidateName}} 👋\n\nYou've been invited to interview for *{{jobTitle}}* at *{{companyName}}*.\n\nAccept: {{acceptUrl}}\nReject: {{rejectUrl}}"
        : getDefaultTemplate("ACCEPTANCE");
    setForm({
      type: "ACCEPTANCE",
      subject: activeChannel === "EMAIL" ? "" : "",
      body: defaultBody,
    });
    setEditorTab("write");
    setShowPreview(false);
  };

  const startEdit = (tpl) => {
    setEditing(tpl);
    setForm({
      type: tpl.templateType || tpl.type,
      subject: tpl.subject,
      body: tpl.body,
    });
    setEditorTab("write");
    setShowPreview(false);
  };

  const cancelEdit = () => {
    setEditing(null);
    setShowPreview(false);
  };

  const saveTemplate = async () => {
    if (activeChannel === "EMAIL" && !form.subject.trim()) return;
    if (!form.body.trim()) return;
    setSaving(true);
    try {
      const all = getAllTemplates();
      if (editing === "new") {
        const newTpl = {
          id: (activeChannel === "EMAIL" ? "et" : "wt") + Date.now(),
          channel: activeChannel,
          templateType: form.type,
          name: form.subject || form.body.substring(0, 40),
          subject: form.subject,
          body: form.body,
          isDefault: false,
          createdAt: new Date().toISOString(),
        };
        all.push(newTpl);
        showToast("Template created");
      } else {
        const idx = all.findIndex((t) => t.id === editing.id);
        if (idx >= 0) {
          all[idx] = {
            ...all[idx],
            templateType: form.type,
            subject: form.subject,
            body: form.body,
            name: form.subject || all[idx].name,
          };
        }
        showToast("Template updated");
      }
      localStorage.setItem("ample_templates", JSON.stringify(all));
      cancelEdit();
      loadTemplates();
    } catch (e) {
      showToast("Failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      const all = getAllTemplates().filter((t) => t.id !== id);
      localStorage.setItem("ample_templates", JSON.stringify(all));
      setTemplates((t) => t.filter((x) => x.id !== id));
      showToast("Template deleted");
    } catch (e) {
      showToast("Delete failed");
    }
  };

  const insertPlaceholder = (key) => {
    const ta = bodyRef.current;
    if (!ta) {
      setForm({ ...form, body: form.body + key });
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newBody =
      form.body.substring(0, start) + key + form.body.substring(end);
    setForm({ ...form, body: newBody });
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + key.length;
    }, 0);
  };

  // Live preview — replace placeholders with sample data
  const getPreviewHtml = useCallback((body) => {
    let html = body || "";
    PLACEHOLDERS.forEach((p) => {
      html = html.replaceAll(
        p.key,
        `<span style="background:#EEF2FF;color:#7B5AC4;padding:1px 4px;border-radius:3px;font-weight:500">${p.example}</span>`,
      );
    });
    return html;
  }, []);

  const loadServerPreview = (tpl) => {
    const html = getPreviewHtml(tpl.body || "");
    setExpandedPreview({ id: tpl.id, html });
  };

  const getDefaultTemplate = (type) => {
    if (type === "ACCEPTANCE")
      return `<h2>Hi {{candidateName}},</h2>
<p>We're excited to invite you for an interview for the <strong>{{jobTitle}}</strong> position at {{companyName}}.</p>
<p><strong>Opens:</strong> {{interviewDate}}<br/><strong>Expires:</strong> {{interviewExpiry}}</p>
<p>Please let us know whether you'd like to accept or decline this invitation before it expires:</p>
<p>
  <a href="{{acceptUrl}}" style="background:#7B5AC4;color:white;padding:10px 24px;border-radius:6px;text-decoration:none;display:inline-block;margin-right:8px">Accept Invitation</a>
  <a href="{{rejectUrl}}" style="background:#EF4444;color:white;padding:10px 24px;border-radius:6px;text-decoration:none;display:inline-block">Reject Invitation</a>
</p>
<p>Best regards,<br/>{{companyName}} Hiring Team</p>`;
    if (type === "REMINDER")
      return `<h2>Hi {{candidateName}},</h2>
<p>This is a friendly reminder that your invitation for <strong>{{jobTitle}}</strong> at <strong>{{companyName}}</strong> is awaiting your response.</p>
<p><strong>Expires:</strong> {{interviewExpiry}}</p>
<p>Please confirm your participation before it expires:</p>
<p><a href="{{acceptUrl}}" style="background:#7B5AC4;color:white;padding:10px 24px;border-radius:6px;text-decoration:none;display:inline-block">Respond Now</a></p>
<p>Best regards,<br/>{{companyName}} Hiring Team</p>`;
    if (type === "INTERVIEW_LINK")
      return `<h2>Hi {{candidateName}},</h2>
<p>Thank you for accepting! Here's your interview link for <strong>{{jobTitle}}</strong>:</p>
<p><a href="{{interviewUrl}}" style="background:#7B5AC4;color:white;padding:10px 24px;border-radius:6px;text-decoration:none;display:inline-block">Start Interview</a></p>
<p>The interview takes 15-25 minutes. Good luck!</p>
<p>{{companyName}} Team</p>`;
    return `<h2>Weekly Hiring Digest</h2><p>Here's your digest for <strong>{{jobTitle}}</strong>.</p><p>— {{companyName}}</p>`;
  };

  const typeBadge = (type) => {
    const t = TEMPLATE_TYPES.find((x) => x.value === type);
    return t ? t.badge : "badge";
  };

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">
            Message Templates
          </h1>
          <p className="text-sm mt-1 text-vf-muted">
            Customize email and WhatsApp templates for invitations, reminders,
            and notifications.
          </p>
        </div>
        <button onClick={startCreate} className="btn-primary btn-sm">
          + Create Template
        </button>
      </div>

      {/* Channel Tabs */}
      <div
        className="flex items-center gap-1 border-b"
        style={{ borderColor: "rgba(99,102,241,0.12)" }}
      >
        {CHANNELS.map((ch) => (
          <button
            key={ch.value}
            onClick={() => {
              setActiveChannel(ch.value);
              cancelEdit();
            }}
            className="px-4 py-2.5 text-sm font-medium transition-all relative"
            style={{
              color: activeChannel === ch.value ? "#7B5AC4" : "#64748B",
              borderBottom:
                activeChannel === ch.value
                  ? "2px solid #7B5AC4"
                  : "2px solid transparent",
              marginBottom: "-1px",
            }}
          >
            {ch.icon} {ch.label}
            <span className="ml-1.5 text-xs opacity-60">
              ({getAllTemplates().filter((t) => t.channel === ch.value).length})
            </span>
          </button>
        ))}
      </div>

      {/* Editor */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="card p-0 overflow-hidden"
          >
            {/* Editor Header */}
            <div
              className="px-5 py-3 flex items-center justify-between bg-vf-tint"
              style={{ borderBottom: "0.5px solid rgba(99,102,241,0.15)" }}
            >
              <h2 className="text-sm font-semibold text-vf-base">
                {editing === "new" ? "Create Template" : "Edit Template"}
              </h2>
              <div className="flex items-center gap-2">
                <button onClick={cancelEdit} className="btn-ghost btn-sm">
                  Cancel
                </button>
                <button
                  onClick={saveTemplate}
                  disabled={saving || !form.subject.trim() || !form.body.trim()}
                  className="btn-primary btn-sm"
                >
                  {saving
                    ? "Saving..."
                    : editing === "new"
                      ? "Create"
                      : "Save Changes"}
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Type + Subject */}
              <div
                className={`grid grid-cols-1 ${activeChannel === "EMAIL" ? "md:grid-cols-3" : "md:grid-cols-2"} gap-3`}
              >
                <div>
                  <label className="input-label">Type</label>
                  <select
                    className="select w-full"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    {TEMPLATE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                {activeChannel === "EMAIL" && (
                  <div className="md:col-span-2">
                    <label className="input-label">Subject Line</label>
                    <input
                      className="input"
                      value={form.subject}
                      onChange={(e) =>
                        setForm({ ...form, subject: e.target.value })
                      }
                      placeholder="e.g. Interview Invitation: {{jobTitle}} at {{companyName}}"
                    />
                  </div>
                )}
                {activeChannel === "WHATSAPP" && (
                  <div>
                    <label className="input-label">Template Name</label>
                    <input
                      className="input"
                      value={form.subject}
                      onChange={(e) =>
                        setForm({ ...form, subject: e.target.value })
                      }
                      placeholder="e.g. Interview Invite"
                    />
                  </div>
                )}
              </div>

              {/* Placeholder chips */}
              <div>
                <label className="input-label mb-2">Insert Variable</label>
                <div className="flex flex-wrap gap-1.5">
                  {PLACEHOLDERS.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => insertPlaceholder(p.key)}
                      className="btn-ghost text-xs px-2 py-1 rounded-lg group relative"
                      title={p.desc}
                    >
                      <code className="text-vf-primary">{p.key}</code>
                    </button>
                  ))}
                </div>
              </div>

              {/* Editor Tabs: Write / Preview */}
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <button
                    onClick={() => setEditorTab("write")}
                    className={`btn-sm text-xs px-3 py-1.5 rounded-lg ${editorTab === "write" ? "bg-vf-base text-white" : "btn-ghost"}`}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="inline mr-1"
                    >
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Write HTML
                  </button>
                  <button
                    onClick={() => setEditorTab("preview")}
                    className={`btn-sm text-xs px-3 py-1.5 rounded-lg ${editorTab === "preview" ? "bg-vf-base text-white" : "btn-ghost"}`}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="inline mr-1"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    Preview
                  </button>
                </div>

                {editorTab === "write" ? (
                  <>
                    <textarea
                      ref={bodyRef}
                      className="textarea w-full font-mono text-xs"
                      rows={activeChannel === "SMS" ? 5 : 14}
                      value={form.body}
                      onChange={(e) => {
                        const next = activeChannel === "SMS"
                          ? e.target.value.slice(0, SMS_LIMIT)
                          : e.target.value;
                        setForm({ ...form, body: next });
                      }}
                      placeholder={
                        activeChannel === "SMS"
                          ? "{{companyName}}: Hi {{candidateName}}, ..."
                          : "<h2>Hi {{candidateName}},</h2>\n<p>You've been invited for...</p>"
                      }
                      style={{ lineHeight: "1.6", tabSize: 2 }}
                    />
                    {activeChannel === "SMS" && (
                      <div className="flex items-center justify-between mt-1 text-[11px]">
                        <span className="text-vf-muted">
                          {form.body.length >= SMS_WARN ? "⚠ Approaching limit" : "Plain text only — no HTML."}
                        </span>
                        <span
                          className={
                            form.body.length >= SMS_LIMIT
                              ? "text-red-600 font-semibold"
                              : form.body.length >= SMS_WARN
                                ? "text-amber-600 font-semibold"
                                : "text-vf-muted"
                          }
                        >
                          {form.body.length} / {SMS_LIMIT}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{ border: "0.5px solid rgba(99,102,241,0.2)" }}
                  >
                    {/* Email preview frame */}
                    <div
                      className="bg-vf-tint px-4 py-2 flex items-center gap-2 text-xs text-vf-muted"
                      style={{
                        borderBottom: "0.5px solid rgba(99,102,241,0.15)",
                      }}
                    >
                      <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                      <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                      <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                      <span className="ml-2">Email Preview</span>
                    </div>
                    <div className="bg-white p-5">
                      <div className="text-xs text-vf-muted mb-1">
                        Subject:{" "}
                        <strong className="text-vf-base">
                          {form.subject || "(no subject)"}
                        </strong>
                      </div>
                      <div
                        className="border-t pt-3 mt-2"
                        style={{ borderColor: "rgba(99,102,241,0.1)" }}
                      >
                        <div
                          className="text-sm leading-relaxed"
                          dangerouslySetInnerHTML={{
                            __html: getPreviewHtml(form.body),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Variable reference */}
              <details className="text-xs">
                <summary className="text-vf-muted cursor-pointer hover:text-vf-primary transition-colors">
                  Variable Reference Guide
                </summary>
                <div className="mt-2 rounded-lg bg-vf-tint p-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className="text-left py-1 text-vf-muted font-medium">
                          Variable
                        </th>
                        <th className="text-left py-1 text-vf-muted font-medium">
                          Description
                        </th>
                        <th className="text-left py-1 text-vf-muted font-medium">
                          Example
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {PLACEHOLDERS.map((p) => (
                        <tr key={p.key}>
                          <td className="py-1">
                            <code className="text-vf-primary">{p.key}</code>
                          </td>
                          <td className="py-1 text-vf-muted">{p.desc}</td>
                          <td className="py-1 text-vf-base">{p.example}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Template List */}
      {loading ? (
        <div className="card p-10 text-center">
          <div className="skeleton h-4 w-24 mx-auto rounded" />
        </div>
      ) : templates.length === 0 ? (
        <div className="card p-10 text-center">
          <p className="text-sm text-vf-muted mb-3">
            No {activeChannel === "WHATSAPP" ? "WhatsApp" : "email"} templates
            yet.
          </p>
          <button onClick={startCreate} className="btn-primary btn-sm">
            Create your first template
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((tpl) => (
            <div key={tpl.id} className="card overflow-hidden">
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={typeBadge(tpl.templateType || tpl.type)}>
                        {tpl.templateType || tpl.type}
                      </span>
                      <span
                        style={{
                          background:
                            tpl.channel === "WHATSAPP"
                              ? "#25D36610"
                              : "#7B5AC410",
                          color:
                            tpl.channel === "WHATSAPP" ? "#25D366" : "#7B5AC4",
                          fontSize: 10,
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 20,
                        }}
                      >
                        {tpl.channel === "WHATSAPP" ? "WhatsApp" : "Email"}
                      </span>
                      {tpl.isDefault && <span className="badge">Default</span>}
                    </div>
                    <h3 className="text-sm font-semibold text-vf-base">
                      {tpl.name || tpl.subject}
                    </h3>
                    <p className="text-xs text-vf-muted mt-1 line-clamp-2">
                      {(tpl.body || "")
                        .replace(/<[^>]*>/g, "")
                        .substring(0, 150)}
                      ...
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => {
                        expandedPreview?.id === tpl.id
                          ? setExpandedPreview(null)
                          : loadServerPreview(tpl);
                      }}
                      className="btn-ghost btn-sm text-xs"
                    >
                      {expandedPreview?.id === tpl.id ? "Hide" : "Preview"}
                    </button>
                    <button
                      onClick={() => startEdit(tpl)}
                      className="btn-secondary btn-sm text-xs"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteTemplate(tpl.id)}
                      className="btn-danger btn-sm text-xs"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded HTML Preview */}
              <AnimatePresence>
                {expandedPreview?.id === tpl.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    style={{ borderTop: "0.5px solid rgba(99,102,241,0.15)" }}
                  >
                    {/* Email client mockup */}
                    <div className="bg-vf-tint px-4 py-2 flex items-center gap-2 text-xs text-vf-muted">
                      <span className="w-2 h-2 rounded-full bg-red-400" />
                      <span className="w-2 h-2 rounded-full bg-yellow-400" />
                      <span className="w-2 h-2 rounded-full bg-green-400" />
                      <span className="ml-2">Rendered Email Preview</span>
                    </div>
                    <div
                      className="bg-white p-6 mx-5 mb-5 mt-2 rounded-xl"
                      style={{ border: "0.5px solid rgba(99,102,241,0.1)" }}
                    >
                      <div className="text-xs text-vf-muted mb-3">
                        <div>
                          From:{" "}
                          <span className="text-vf-base">
                            noreply@amplehire.com
                          </span>
                        </div>
                        <div>
                          To:{" "}
                          <span className="text-vf-base">
                            candidate@example.com
                          </span>
                        </div>
                        <div>
                          Subject:{" "}
                          <strong className="text-vf-base">
                            {tpl.subject}
                          </strong>
                        </div>
                      </div>
                      <div
                        className="border-t pt-3"
                        style={{ borderColor: "rgba(99,102,241,0.1)" }}
                      >
                        <div
                          className="text-sm leading-relaxed"
                          dangerouslySetInnerHTML={{
                            __html: expandedPreview.html,
                          }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 z-[9999] card px-5 py-3 text-sm font-medium text-vf-base"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmailTemplates;
